import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Card,
  Row,
  Col,
  Space,
  Tag,
  Spin,
  message,
  Avatar,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Table,
  Empty,
  InputNumber,
  Alert,
  Badge,
  Progress,
  Tabs,
  Breadcrumb,
  Divider,
  Segmented
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  UserOutlined,
  ShoppingOutlined,
  MailOutlined,
  IdcardOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  FilterOutlined,
  ReloadOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  BankOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  DollarCircleOutlined,
  ShoppingCartOutlined,
  TransactionOutlined,
  DashboardOutlined,
  HomeOutlined,
  SearchOutlined,
  WalletOutlined,
  ContactsOutlined,
  EuroOutlined,
  CalculatorOutlined,
  CloseOutlined,
  MobileOutlined,
  DollarOutlined,
  CreditCardOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import api from '../../../utils/axios';
import { StatCard, InfoItem, TransactionCard } from './ClientComponents';
import { transactionColumns } from './TransactionColumns';
import VersementModal from './VersementModal';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

// Configurer dayjs en français
dayjs.locale('fr');

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [validationPaiementForm] = Form.useForm();
 
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [validationPaiementModalVisible, setValidationPaiementModalVisible] = useState(false);
  const [versementModalVisible, setVersementModalVisible] = useState(false);
  const [selectedVenteForVersement, setSelectedVenteForVersement] = useState(null);
  const [selectedVenteForValidation, setSelectedVenteForValidation] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [stats, setStats] = useState({
    totalVentes: 0,
    totalMontant: 0,
    totalQuantite: 0,
    montantRestant: 0,
    montantTotalAttente: 0,
    montantTotalVerse: 0,
    lastTransactionDate: null,
    ventesEnAttente: 0,
    ventesValidees: 0,
    ventesPayees: 0,
    totalVersements: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [transactionFilters, setTransactionFilters] = useState({
    dateRange: null,
    statut: '',
    search: ''
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [viewMode, setViewMode] = useState('detailed');
  const [exportLoading, setExportLoading] = useState(false);

  // ==================== FONCTIONS CORRIGÉES POUR LES CALCULS ====================

  // Fonction pour charger les paiements d'un client
  const loadPaiements = async () => {
    try {
      console.log('🔍 Chargement des paiements pour client ID:', id);
     
      // Appel API pour récupérer les paiements du client
      const response = await api.get(`/api/paiements/client/${id}`);
     
      if (response.data.success) {
        const paiementsData = response.data.data?.paiements || response.data.data || [];
        console.log('✅ Paiements chargés:', paiementsData.length, 'paiements');
       
        // Stocker les paiements
        setPaiements(paiementsData);
       
        // Organiser les paiements par venteId
        const paiementsParVente = {};
        paiementsData.forEach(paiement => {
          const venteId = paiement.venteId?.toString();
          if (venteId) {
            if (!paiementsParVente[venteId]) {
              paiementsParVente[venteId] = {
                montantVerse: 0,
                paiements: []
              };
            }
            paiementsParVente[venteId].montantVerse += parseFloat(paiement.montant) || 0;
            paiementsParVente[venteId].paiements.push(paiement);
          }
        });
       
        return paiementsParVente;
      } else {
        console.error('❌ Erreur chargement paiements:', response.data.message);
        return {};
      }
    } catch (error) {
      console.error('❌ Erreur chargement paiements:', error);
      return {};
    }
  };

  // Fonction pour calculer le montant restant d'une vente spécifique
  const calculateMontantRestant = (vente, paiementsList = paiements) => {
    if (!vente) {
      return {
        totalVerse: 0,
        montantRestant: 0,
        isComplete: false,
        pourcentage: 0
      };
    }

    const venteId = (vente.id || vente._id || '').toString();
   
    if (!venteId) {
      const montantTotal = parseFloat(vente.montant) || 0;
      return {
        totalVerse: 0,
        montantRestant: montantTotal,
        isComplete: montantTotal <= 0,
        pourcentage: 0
      };
    }

    // Récupérer les paiements pour cette vente spécifique
    const paiementsVente = Array.isArray(paiementsList)
      ? paiementsList.filter(p => {
          if (!p || !p.venteId) return false;
          const paiementVenteId = p.venteId.toString();
          return paiementVenteId === venteId;
        })
      : [];

    // FORMULE CORRECTE: montantRestant = montantTotal - montantVerse
    const totalVerse = paiementsVente.reduce((sum, p) => sum + (parseFloat(p.montant) || 0), 0);
    const montantTotal = parseFloat(vente.montant) || 0;
    const montantRestant = Math.max(0, montantTotal - totalVerse);
    const pourcentage = montantTotal > 0 ? Math.round((totalVerse / montantTotal) * 100) : 0;

    return {
      totalVerse,
      montantRestant,
      isComplete: montantRestant <= 0,
      pourcentage,
      paiements: paiementsVente
    };
  };

  // Fonction principale pour charger les statistiques
  const loadClientStats = async () => {
    setLoadingStats(true);
    try {
      console.log('📊 Chargement des statistiques pour client:', id);
     
      // 1. Charger les ventes du client
      const ventesResponse = await api.get(`/api/ventes/client/${id}`, {
        params: { limit: 1000 }
      });
     
      if (!ventesResponse.data.success) {
        throw new Error('Erreur lors du chargement des ventes');
      }
     
      const transactionsData = Array.isArray(ventesResponse.data.data?.ventes || ventesResponse.data.data)
        ? (ventesResponse.data.data.ventes || ventesResponse.data.data)
        : [];
     
      // 2. Charger les paiements
      const paiementsParVente = await loadPaiements();
     
      // 3. Calculer les statistiques
      const totalVentes = transactionsData.length;
      const totalMontant = transactionsData.reduce((sum, v) => sum + (parseFloat(v.montant) || 0), 0);
      const totalQuantite = transactionsData.reduce((sum, v) => sum + (parseFloat(v.quantite) || 0), 0);
     
      // Compter par statut
      const ventesEnAttente = transactionsData.filter(v => v.statut === 'En attente').length;
      const ventesValidees = transactionsData.filter(v => v.statut === 'Validée').length;
      const ventesPayees = transactionsData.filter(v => v.statut === 'Payée').length;
     
      // Montant des ventes en attente
      const montantEnAttente = transactionsData
        .filter(v => v.statut === 'En attente')
        .reduce((sum, v) => sum + (parseFloat(v.montant) || 0), 0);
     
      // Calculer le montant total versé à partir des paiements
      const totalVerse = Object.values(paiementsParVente).reduce(
        (sum, p) => sum + (p.montantVerse || 0), 0
      );
     
      // Calculer le montant restant total selon la formule: montantRestant = montantTotal - montantVerse
      let montantRestantTotal = 0;
     
      transactionsData.forEach(vente => {
        const venteId = (vente.id || vente._id)?.toString();
        const montantTotal = parseFloat(vente.montant) || 0;
        const montantVerse = paiementsParVente[venteId]?.montantVerse || 0;
       
        // FORMULE CORRECTE: montantRestant = montantTotal - montantVerse
        const montantRestant = Math.max(0, montantTotal - montantVerse);
        montantRestantTotal += montantRestant;
       
        console.log(`📈 Calcul pour vente ${vente.numeroFacture}:`);
        console.log(`  - Montant total: ${montantTotal}`);
        console.log(`  - Montant versé: ${montantVerse}`);
        console.log(`  - Montant restant: ${montantRestant}`);
        console.log(`  - Formule: ${montantTotal} - ${montantVerse} = ${montantRestant}`);
      });
     
      // Date de la dernière transaction
      const dernieresVentes = [...transactionsData]
        .filter(v => v.dateFacture || v.createdAt)
        .sort((a, b) => new Date(b.dateFacture || b.createdAt) - new Date(a.dateFacture || a.createdAt));
     
      const lastTransactionDate = dernieresVentes.length > 0
        ? (dernieresVentes[0].dateFacture || dernieresVentes[0].createdAt)
        : null;
     
      console.log('📊 Statistiques finales calculées:');
      console.log('- Total ventes:', totalVentes);
      console.log('- Total montant:', totalMontant.toLocaleString('fr-FR'));
      console.log('- Total versé:', totalVerse.toLocaleString('fr-FR'));
      console.log('- Montant restant total:', montantRestantTotal.toLocaleString('fr-FR'));
      console.log('- Formule vérifiée: montantRestant = montantTotal - montantVerse');
     
      // Mettre à jour les statistiques
      setStats({
        totalVentes,
        totalMontant,
        totalQuantite,
        montantRestant: montantRestantTotal,
        montantTotalAttente: montantEnAttente,
        montantTotalVerse: totalVerse,
        ventesEnAttente,
        ventesValidees,
        ventesPayees,
        totalVersements: paiements.length,
        lastTransactionDate
      });
     
    } catch (error) {
      console.error('❌ Erreur chargement statistiques:', error);
      message.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoadingStats(false);
    }
  };

  // Fonction pour charger et formater les transactions
  const loadTransactions = async (page = 1) => {
    if (!client) return;
   
    setTransactionsLoading(true);
    try {
      console.log('📋 Chargement des transactions, page:', page);
     
      const params = {
        page: page,
        limit: pagination.pageSize,
        sortBy: 'dateFacture',
        sortOrder: 'desc',
        search: transactionFilters.search || undefined
      };

      // Filtres de date
      if (transactionFilters.dateRange && transactionFilters.dateRange[0] && transactionFilters.dateRange[1]) {
        params.startDate = transactionFilters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = transactionFilters.dateRange[1].format('YYYY-MM-DD');
      }

      // Filtre de statut
      if (transactionFilters.statut) {
        params.statut = transactionFilters.statut;
      }

      // Appel API pour les transactions
      const response = await api.get(`/api/ventes/client/${id}`, { params });
     
      if (response.data.success) {
        let transactionsData = Array.isArray(response.data.data?.ventes)
          ? response.data.data.ventes
          : (Array.isArray(response.data.data) ? response.data.data : []);
         
        console.log('✅ Transactions chargées:', transactionsData.length);
       
        // Charger les paiements pour calculer les montants
        const paiementsParVente = await loadPaiements();
       
        // Formater les transactions avec les calculs corrects
        const formattedTransactions = transactionsData.map(vente => {
          const venteId = (vente.id || vente._id)?.toString();
          const montantTotal = parseFloat(vente.montant) || 0;
         
          // Récupérer les paiements de cette vente
          const paiementsVente = paiementsParVente[venteId];
          const montantVerse = paiementsVente?.montantVerse || 0;
         
          // FORMULE CORRECTE: montantRestant = montantTotal - montantVerse
          const montantRestant = Math.max(0, montantTotal - montantVerse);
          const isComplete = montantRestant <= 0;
         
          return {
            id: venteId,
            _id: vente._id || vente.id || '',
            type: 'vente',
            typeLabel: 'Vente',
            typeColor: 'blue',
            typeIcon: <ShoppingOutlined />,
            numero: vente.numeroFacture || `FACT-${(vente.id || vente._id || '').toString().substring(0, 8)}`,
            designation: vente.designation || 'Vente non spécifiée',
            montant: montantTotal,
            montantVerse: montantVerse,
            montantRestant: montantRestant,
            isComplete: isComplete,
            quantite: parseFloat(vente.quantite) || 0,
            chauffeur: vente.chauffeur || 'Non spécifié',
            statut: vente.statut || 'En attente',
            date: vente.dateFacture || vente.createdAt,
            dateFormatted: vente.dateFormatted || (vente.dateFacture
              ? dayjs(vente.dateFacture).format('DD/MM/YYYY HH:mm')
              : dayjs(vente.createdAt).format('DD/MM/YYYY HH:mm')),
            modePaiement: vente.modePaiement || 'Espèces',
            note: vente.note || '',
            clientId: vente.clientId || id,
            clientName: vente.client || `${client?.prenom} ${client?.nom}`,
            hasVersements: montantVerse > 0,
            paiements: paiementsVente?.paiements || []
          };
        });
       
        setTransactions(formattedTransactions);
       
        // Mettre à jour la pagination
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.data.data.pagination?.totalItems || formattedTransactions.length
        }));
       
      } else {
        console.error('❌ Erreur chargement transactions:', response.data.message);
        message.error(response.data.message || 'Erreur lors du chargement des transactions');
        setTransactions([]);
      }
     
    } catch (error) {
      console.error('❌ Erreur chargement transactions:', error);
      message.error('Erreur lors du chargement des transactions');
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  // ==================== FONCTIONS POUR VERSEMENT ====================

  const handleVersementClick = (vente) => {
    const { totalVerse, montantRestant } = calculateMontantRestant(vente, paiements);
   
    setSelectedVenteForVersement({
      ...vente,
      montantVerse: totalVerse,
      montantRestant: montantRestant
    });
    setVersementModalVisible(true);
  };

  const handleVersementSubmit = async (paiementData) => {
    try {
      console.log('📤 Envoi du versement:', paiementData);
     
      const response = await api.post('/api/paiements/versement', paiementData);
     
      if (response.data.success) {
        message.success('Versement enregistré avec succès');
        setVersementModalVisible(false);
        setSelectedVenteForVersement(null);
       
        // Recharger toutes les données
        await loadPaiements();
        await loadTransactions(pagination.current);
        await loadClientStats();
       
        console.log('✅ Paiement créé avec succès');
      } else {
        message.error(response.data.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('❌ Erreur enregistrement versement:', error);
     
      if (error.response) {
        const errorMessage = error.response.data?.message ||
                            (error.response.data?.errors ? error.response.data.errors.join(', ') : 'Erreur serveur');
        message.error(`Erreur serveur: ${errorMessage}`);
      } else if (error.request) {
        message.error('Le serveur ne répond pas. Vérifiez votre connexion.');
      } else {
        message.error(`Erreur: ${error.message}`);
      }
    }
  };

  const handleMontantVerse = (vente) => {
    const { totalVerse, montantRestant } = calculateMontantRestant(vente, paiements);
   
    const venteWithCalculs = {
      ...vente,
      montantVerse: totalVerse,
      montantRestant: montantRestant
    };
   
    handleVersementClick(venteWithCalculs);
  };

  // ==================== FONCTIONS POUR VALIDATION PAIEMENT ====================

  const handleValidationPaiement = (vente) => {
    setSelectedVenteForValidation(vente);
    setValidationPaiementModalVisible(true);
  };

  const validerPaiementComplet = async (values) => {
    try {
      if (!selectedVenteForValidation) {
        message.error('Aucune vente sélectionnée');
        return;
      }

      const validationData = {
        venteId: selectedVenteForValidation.id,
        numeroFacture: selectedVenteForValidation.numero,
        montantVente: selectedVenteForValidation.montant,
        modePaiement: values.modePaiement,
        nomBanque: values.nomBanque,
        numeroReference: values.numeroReference,
        datePaiement: values.datePaiement ? values.datePaiement.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        montantPaye: selectedVenteForValidation.montantRestant,
        note: values.note || '',
        clientId: id,
        clientName: `${client.prenom} ${client.nom}`,
        typeValidation: 'complet'
      };

      const response = await api.post('/api/paiements/validation', validationData);
     
      if (response.data.success) {
        message.success('Paiement validé avec succès');
        setValidationPaiementModalVisible(false);
        setSelectedVenteForValidation(null);
        validationPaiementForm.resetFields();
       
        // Recharger les données
        await loadPaiements();
        await loadTransactions(pagination.current);
        await loadClientStats();
      } else {
        message.error(response.data.message || 'Erreur lors de la validation');
      }
    } catch (error) {
      console.error('❌ Erreur validation paiement:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la validation du paiement');
    }
  };

  const verifierSiMontantVerseExiste = (vente) => {
    if (!Array.isArray(paiements) || !vente) return false;
   
    const paiementsVente = paiements.filter(p => {
      const paiementVenteId = p.venteId?.toString();
      const venteId = (vente.id || vente._id)?.toString();
      return paiementVenteId === venteId;
    });
   
    return paiementsVente.length > 0;
  };

  // ==================== AUTRES FONCTIONS ESSENTIELLES ====================

  const loadClient = async () => {
    setLoading(true);
    try {
      console.log('👤 Chargement du client ID:', id);
     
      const clientResponse = await api.get(`/api/clients/${id}`);
     
      if (!clientResponse.data.success) {
        throw new Error(clientResponse.data.message || 'Client non trouvé');
      }
     
      const clientData = clientResponse.data.data || clientResponse.data;
     
      if (!clientData) {
        throw new Error('Données client non disponibles');
      }
     
      console.log('✅ Client chargé:', clientData);
      setClient(clientData);
     
      // Charger les paiements d'abord
      await loadPaiements();
     
      // Charger les statistiques
      await loadClientStats();
     
      // Si l'onglet transactions est actif, charger les transactions
      if (activeTab === 'transactions') {
        await loadTransactions(1);
      }
     
    } catch (error) {
      console.error('❌ Erreur chargement client:', error);
      message.error(error.response?.data?.message || 'Erreur lors du chargement du client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const formatTelephone = (telephone) => {
    if (!telephone) return '';
    const cleaned = telephone.toString().replace(/\D/g, '');
    if (cleaned.length === 12 && cleaned.startsWith('221')) {
      const part1 = cleaned.slice(0, 3);
      const part2 = cleaned.slice(3, 5);
      const part3 = cleaned.slice(5, 8);
      const part4 = cleaned.slice(8, 10);
      const part5 = cleaned.slice(10, 12);
      return `+${part1} ${part2} ${part3} ${part4} ${part5}`;
    }
    return telephone;
  };

  const updateClient = async (values) => {
    try {
      const response = await api.put(`/api/clients/${id}`, values);
     
      if (response.data.success) {
        message.success('Client mis à jour avec succès');
        const updatedClient = response.data.data || response.data;
        setClient(updatedClient);
        setEditModalVisible(false);
        loadClient();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const getPendingTransactions = () => {
    return Array.isArray(transactions)
      ? transactions.filter(t => t.statut === 'En attente')
      : [];
  };

  const redirectToNewSale = () => {
    navigate('/ventes/new', {
      state: {
        clientId: id,
        clientName: `${client.prenom} ${client.nom}`,
        redirectBack: `/clients/${id}?tab=transactions`
      }
    });
  };

  const handleTransactionFilterChange = (field, value) => {
    setTransactionFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyTransactionFilters = () => {
    loadTransactions(1);
  };

  const resetTransactionFilters = () => {
    setTransactionFilters({
      dateRange: null,
      statut: '',
      search: ''
    });
    loadTransactions(1);
  };

  const viewVenteDetail = (venteId) => {
    if (!venteId) {
      message.error('ID de vente invalide');
      return;
    }
    navigate(`/ventes/${venteId}`);
  };

  const exportTransactions = async () => {
    if (!Array.isArray(transactions) || transactions.length === 0) {
      message.warning('Aucune transaction à exporter');
      return;
    }

    setExportLoading(true);
    try {
      const csvContent = [
        ['Client', 'Numéro Facture', 'Désignation', 'Montant Total', 'Montant Versé', 'Montant Restant', 'Quantité', 'Chauffeur', 'Statut', 'Date', 'Mode paiement', 'Note'],
        ...transactions.map(t => [
          `${client?.prenom} ${client?.nom}`,
          t.numero || '',
          t.designation || '',
          t.montant || 0,
          t.montantVerse || 0,
          t.montantRestant || 0,
          t.quantite || 0,
          t.chauffeur || '',
          t.statut || '',
          t.dateFormatted || '',
          t.modePaiement || '',
          t.note || ''
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${client?.prenom}_${client?.nom}_${dayjs().format('YYYYMMDD')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
     
      message.success('Transactions exportées avec succès');
    } catch (error) {
      console.error('Erreur export:', error);
      message.error('Erreur lors de l\'exportation');
    } finally {
      setExportLoading(false);
    }
  };

  const getClientStatusColor = () => {
    if (stats.montantTotalAttente > 0) return 'warning';
    if (stats.totalVentes === 0) return 'default';
    return 'success';
  };

  const getClientStatusText = () => {
    if (stats.montantTotalAttente > 0) return `En attente: ${stats.montantTotalAttente.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}`;
    if (stats.totalVentes === 0) return 'Nouveau client';
    return 'Client actif';
  };

  // ==================== USEEFFECT ====================

  useEffect(() => {
    if (id) {
      loadClient();
    }
  }, [id]);

  useEffect(() => {
    if (client && activeTab === 'transactions') {
      const timer = setTimeout(() => {
        loadTransactions(1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTab, client]);

  // ==================== RENDERING CONDITIONNEL ====================

  if (loading && !client) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Spin size="large" tip="Chargement du client..." />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <CloseCircleOutlined className="text-red-500 text-5xl mb-4" />
          <Title level={3} className="text-gray-800 mb-2">Client non trouvé</Title>
          <Text className="text-gray-600 mb-6 block">
            Le client que vous recherchez n'existe pas ou a été supprimé.
          </Text>
          <Button type="primary" onClick={() => navigate('/clients')}>
            Retour à la liste
          </Button>
        </div>
      </div>
    );
  }

  const fullName = `${client.prenom} ${client.nom}`;
  const pendingTransactions = getPendingTransactions();

  // ==================== SECTIONS DE CONTENU ====================

  const renderInfoTab = () => (
    <div className="space-y-6">
      {/* Informations personnelles */}
      <Card
        style={{ borderRadius: '2px', border: '1px solid #1f303f' }}
        title={
          <div className="flex items-center">
            <ContactsOutlined className="text-blue-500 mr-2" />
            <span>Informations personnelles</span>
          </div>
        }
        className="shadow-sm"
        extra={
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditModalVisible(true)}
          >
            Modifier
          </Button>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <InfoItem
              icon={<UserOutlined />}
              label="Nom complet"
              value={fullName}
              color="blue"
            />
            <Divider className="my-4" />
            <InfoItem
              icon={<PhoneOutlined />}
              label="Téléphone"
              value={formatTelephone(client.telephone)}
              color="green"
              action={{
                icon: <PhoneOutlined />,
                text: 'Appeler',
                onClick: () => window.location.href = `tel:${client.telephone}`
              }}
            />
            <Divider className="my-4" />
            {client.email && (
              <>
                <InfoItem
                  icon={<MailOutlined />}
                  label="Email"
                  value={client.email}
                  color="orange"
                  action={{
                    icon: <MailOutlined />,
                    text: 'Envoyer email',
                    onClick: () => window.location.href = `mailto:${client.email}`
                  }}
                />
                <Divider className="my-4" />
              </>
            )}
          </Col>
          <Col xs={24} md={12}>
            <InfoItem
              icon={<EnvironmentOutlined />}
              label="Adresse"
              value={client.adresse || "Non spécifiée"}
              color="purple"
            />
            <Divider className="my-4" />
            <InfoItem
              icon={<CalendarOutlined />}
              label="Client depuis"
              value={dayjs(client.dateCreation || client.createdAt).format('DD MMMM YYYY')}
              color="red"
            />
            <Divider className="my-4" />
            <InfoItem
              icon={<IdcardOutlined />}
              label="ID Client"
              value={`#${(client.id || client._id || '').toString().substring(0, 8).toUpperCase()}`}
              color="cyan"
            />
          </Col>
        </Row>
      </Card>

      {/* Statistiques */}
      <Card
        style={{ borderRadius: '2px', border: '1px solid #1f303f' }}
        title={
          <div className="flex items-center">
            <DashboardOutlined className="text-green-500 mr-2" />
            <span>Statistiques du client</span>
          </div>
        }
        className="shadow-sm"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              title="Ventes totales"
              value={stats.totalVentes}
              icon={<ShoppingCartOutlined />}
              color="blue"
              format="number"
              description="Nombre total de transactions"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              title="Chiffre d'affaires"
              value={stats.totalMontant}
              icon={<DollarCircleOutlined />}
              color="green"
              format="currency"
              description="CA total généré"
              showOnlyIfValidated={true}
              ventesValidees={stats.ventesValidees}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              title="Montant Restant"
              value={stats.montantRestant}
              icon={<WalletOutlined />}
              color="orange"
              format="currency"
              description="Total à régler selon formule: montantTotal - montantVersé"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              title="Montant Versé"
              value={stats.montantTotalVerse}
              icon={<CreditCardOutlined />}
              color="purple"
              format="currency"
              description="Total déjà versé par le client"
            />
          </Col>
        </Row>
       
        <Divider />
       
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{stats.ventesValidees}</div>
              <Text className="text-gray-600">Ventes validées</Text>
            </div>
          </Col>
          <Col xs={24} md={6}>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-3xl font-bold text-orange-600">{stats.ventesEnAttente}</div>
              <Text className="text-gray-600">Ventes en attente</Text>
            </div>
          </Col>
          <Col xs={24} md={6}>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-3xl font-bold text-green-600">{stats.totalQuantite}</div>
              <Text className="text-gray-600">Tonnes vendues</Text>
            </div>
          </Col>
          <Col xs={24} md={6}>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-3xl font-bold text-purple-600">{stats.totalVersements}</div>
              <Text className="text-gray-600">Versements</Text>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );

  const renderTransactionsTab = () => {
    // Calculer le montant restant total selon la formule
    const montantRestantTotal = Array.isArray(transactions)
      ? transactions
          .filter(t => t.statut === 'Validée' || t.statut === 'Payée')
          .reduce((sum, t) => {
            const montantTotal = t.montant || 0;
            const montantVerse = t.montantVerse || 0;
            const montantRestant = Math.max(0, montantTotal - montantVerse);
            return sum + montantRestant;
          }, 0)
      : 0;

    return (
      <div className="space-y-6">
        {/* En-tête et filtres */}
        <Card className="shadow-sm" style={{ borderRadius: '2px', border: '1px solid #0073ff'}}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div>
              <Title level={4} className="mb-1">Historique des transactions</Title>
              <Text className="text-gray-500">
                Toutes les ventes de {fullName}
              </Text>
            </div>
          </div>

          {/* Mode de vue */}
          <div className="flex justify-between items-center mb-6">
            <Segmented
              options={[
                { label: 'Vue détaillée', value: 'detailed', icon: <AppstoreOutlined /> },
                { label: 'Vue compacte', value: 'compact', icon: <DashboardOutlined /> }
              ]}
              value={viewMode}
              onChange={setViewMode}
              size="large"
            />
          </div>

          {/* Filtres */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Text className="text-sm text-gray-600 mb-1 block">Recherche</Text>
                <Input
                  placeholder="Numéro, désignation..."
                  value={transactionFilters.search}
                  onChange={(e) => handleTransactionFilterChange('search', e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  allowClear
                />
              </div>
              <div>
                <Text className="text-sm text-gray-600 mb-1 block">Période</Text>
                <RangePicker
                  value={transactionFilters.dateRange}
                  onChange={(dates) => handleTransactionFilterChange('dateRange', dates)}
                  className="w-full"
                  format="DD/MM/YYYY"
                  placeholder={['Date début', 'Date fin']}
                />
              </div>
              <div>
                <Text className="text-sm text-gray-600 mb-1 block">Statut</Text>
                <Select
                  value={transactionFilters.statut}
                  onChange={(value) => handleTransactionFilterChange('statut', value)}
                  className="w-full"
                  placeholder="Tous les statuts"
                  allowClear
                >
                  <Option value="En attente">En attente</Option>
                  <Option value="Validée">Validée</Option>
                  <Option value="Payée">Payée</Option>
                  <Option value="Annulée">Annulée</Option>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={resetTransactionFilters}
                  className="border-gray-300"
                >
                  Réinitialiser
                </Button>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={applyTransactionFilters}
                  loading={transactionsLoading}
                >
                  Appliquer
                </Button>
              </div>
            </div>
          </div>

          {/* Statistiques rapides avec formule */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <Text className="text-blue-600 text-sm">Total transactions</Text>
              <div className="text-xl font-bold text-blue-700">{transactions.length}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <Text className="text-green-600 text-sm">Montant total validé</Text>
              <div className="text-xl font-bold text-green-700">
                {Array.isArray(transactions)
                  ? transactions
                    .filter(t => t.statut === 'Validée' || t.statut === 'Payée')
                    .reduce((sum, t) => sum + (t.montant || 0), 0)
                    .toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: 'XOF',
                      minimumFractionDigits: 0
                    })
                  : '0'
                }
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <Text className="text-orange-600 text-sm">Montant restant</Text>
              <div className="text-xl font-bold text-orange-700">
                {montantRestantTotal.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                <div className="text-xs text-orange-500 mt-1">Formule: Total - Versé</div>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <Text className="text-purple-600 text-sm">Dernière transaction</Text>
              <div className="text-xl font-bold text-purple-700">
                {Array.isArray(transactions) && transactions.length > 0 && transactions[0].date
                  ? dayjs(transactions[0].date).format('DD/MM/YY')
                  : 'Aucune'
                }
              </div>
            </div>
          </div>
        </Card>

        {/* Table ou liste des transactions */}
        {viewMode === 'detailed' ? (
          <Card className="shadow-sm" style={{ borderRadius: '2px', border: '1px solid #0073ff'}}>
            <Table
              columns={transactionColumns.map(col => ({
                ...col,
                render: (text, record, index) => {
                  if (col.render) {
                    return col.render(text, record, {
                      calculateMontantRestant: () => calculateMontantRestant(record, paiements),
                      verifierSiMontantVerseExiste: () => verifierSiMontantVerseExiste(record),
                      handleValidationPaiement: () => handleValidationPaiement(record),
                      handleMontantVerse: () => handleMontantVerse(record),
                      viewVenteDetail
                    });
                  }
                  return text;
                }
              }))}
              dataSource={Array.isArray(transactions) ? transactions : []}
              rowKey="id"
              loading={transactionsLoading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => (
                  <div className="text-gray-600">
                    {range[0]}-{range[1]} sur {total} transactions
                  </div>
                ),
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                  loadTransactions(page);
                }
              }}
              locale={{
                emptyText: transactionsLoading ? (
                  <div className="py-12">
                    <Spin size="large" />
                    <div className="mt-4 text-gray-500">Chargement des transactions...</div>
                  </div>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div className="text-center">
                        <div className="text-gray-600 mb-2">Aucune transaction trouvée</div>
                        <Text className="text-gray-500 mb-4">
                          {transactionFilters.dateRange || transactionFilters.statut || transactionFilters.search
                            ? "Essayez de modifier vos filtres de recherche."
                            : "Ce client n'a pas encore effectué de transactions."
                          }
                        </Text>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={redirectToNewSale}
                        >
                          Créer une première vente
                        </Button>
                      </div>
                    }
                  />
                )
              }}
            />
          </Card>
        ) : (
          <div className="space-y-4">
            {Array.isArray(transactions) && transactions.map(transaction => (
              <TransactionCard
                key={transaction.id}
                transaction={transaction}
                calculateMontantRestant={() => calculateMontantRestant(transaction, paiements)}
                verifierSiMontantVerseExiste={() => verifierSiMontantVerseExiste(transaction)}
                handleValidationPaiement={handleValidationPaiement}
                handleMontantVerse={handleMontantVerse}
                viewVenteDetail={viewVenteDetail}
              />
            ))}
            {(!Array.isArray(transactions) || transactions.length === 0) && !transactionsLoading && (
              <Card
                style={{ borderRadius: '2px', border: '1px solid #0088ff'}}
                className="text-center py-12">
                <Empty
                  description={
                    <div>
                      <div className="text-gray-600 mb-2">Aucune transaction trouvée</div>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={redirectToNewSale}
                      >
                        Créer une première vente
                      </Button>
                    </div>
                  }
                />
              </Card>
            )}
            {transactionsLoading && (
              <Card style={{ borderRadius: '2px', border: '1px solid #0073ff'}} className="text-center py-12">
                <Spin size="large" />
                <div className="mt-4 text-gray-500">Chargement des transactions...</div>
              </Card>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderValidationTab = () => {
    const ventesValideesAvecRestant = Array.isArray(transactions)
      ? transactions.filter(t =>
          t.statut === 'Validée' && (t.montantRestant || 0) > 0
        )
      : [];
    const totalMontantRestant = ventesValideesAvecRestant.reduce((sum, t) => sum + (t.montantRestant || 0), 0);
   
    const ventesAvecVersements = ventesValideesAvecRestant.filter(t =>
      verifierSiMontantVerseExiste(t)
    );

    return (
      <div className="space-y-6">
        <Card className="shadow-sm" style={{ borderRadius: '2px', border: '1px solid #0073ff'}}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div>
              <Title level={4} className="mb-1">Gestion des versements et validation</Title>
              <Text className="text-gray-500">
                Enregistrement et validation des paiements pour {fullName}
              </Text>
            </div>
            {ventesValideesAvecRestant.length > 0 && (
              <Space>
                {ventesAvecVersements.length > 0 && (
                  <Button
                    type="primary"
                    danger
                    icon={<CheckCircleOutlined />}
                    onClick={() => {
                      if (ventesAvecVersements.length > 0) {
                        setSelectedVenteForValidation(ventesAvecVersements[0]);
                        setValidationPaiementModalVisible(true);
                      }
                    }}
                  >
                    Valider un paiement
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<DollarCircleOutlined />}
                  onClick={() => {
                    if (ventesValideesAvecRestant.length > 0) {
                      const vente = ventesValideesAvecRestant[0];
                      const { totalVerse, montantRestant } = calculateMontantRestant(vente, paiements);
                      handleVersementClick({
                        ...vente,
                        montantVerse: totalVerse,
                        montantRestant: montantRestant
                      });
                    }
                  }}
                >
                  Enregistrer un versement
                </Button>
              </Space>
            )}
          </div>

          <Alert
            message={
              ventesValideesAvecRestant.length > 0
                ? `Ventes en cours de paiement (${ventesValideesAvecRestant.length})`
                : 'Tous les montants sont réglés'
            }
            description={
              ventesValideesAvecRestant.length > 0
                ? `${fullName} a ${ventesValideesAvecRestant.length} vente(s) avec montant restant pour un total de ${totalMontantRestant.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}.`
                : `Toutes les ventes de ${fullName} sont entièrement réglées.`
            }
            type={ventesValideesAvecRestant.length > 0 ? 'warning' : 'success'}
            showIcon
            icon={ventesValideesAvecRestant.length > 0 ? <WalletOutlined /> : <CheckCircleOutlined />}
            className="mb-6"
          />

          {ventesValideesAvecRestant.length > 0 ? (
            <div className="space-y-4">
              {ventesValideesAvecRestant.map(transaction => {
                const hasVersements = verifierSiMontantVerseExiste(transaction);
               
                return (
                  <div key={transaction.id} className="border border-gray-200 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <ShoppingOutlined className="text-blue-500 mr-2" />
                          <Text strong className="text-lg">{transaction.designation}</Text>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span><FileTextOutlined className="mr-1" /> {transaction.numero}</span>
                          <span><CalendarOutlined className="mr-1" /> {transaction.dateFormatted}</span>
                          {transaction.chauffeur && (
                            <span><UserOutlined className="mr-1" /> {transaction.chauffeur}</span>
                          )}
                        </div>
                       
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div className="p-2 bg-blue-50 rounded">
                            <Text className="text-xs text-gray-500">Montant Total</Text>
                            <div className="font-bold text-blue-700">{(transaction.montant || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}</div>
                          </div>
                          <div className="p-2 bg-green-50 rounded">
                            <Text className="text-xs text-gray-500">Déjà Versé</Text>
                            <div className="font-bold text-green-700">{(transaction.montantVerse || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}</div>
                          </div>
                          <div className="p-2 bg-orange-50 rounded">
                            <Text className="text-xs text-gray-500">À Régler</Text>
                            <div className="font-bold text-orange-700">
                              {(transaction.montantRestant || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                              <div className="text-xs text-orange-500 mt-1">Formule: Total - Versé</div>
                            </div>
                          </div>
                        </div>
                       
                        {hasVersements && (
                          <Alert
                            message="Versements enregistrés"
                            description="Cette vente a des versements en attente de validation complète."
                            type="info"
                            showIcon
                            icon={<CheckCircleOutlined />}
                            className="mt-3"
                          />
                        )}
                      </div>
                      <div className="text-right">
                        <div className="mb-4">
                          <Progress
                            percent={Math.round(((transaction.montantVerse || 0) / (transaction.montant || 1)) * 100)}
                            strokeColor="#10b981"
                            trailColor="#f3f4f6"
                            size="large"
                          />
                        </div>
                        <div className="mt-3 space-y-2">
                          {hasVersements ? (
                            <Button
                              type="primary"
                              danger
                              icon={<CheckCircleOutlined />}
                              onClick={() => {
                                setSelectedVenteForValidation(transaction);
                                setValidationPaiementModalVisible(true);
                              }}
                              block
                            >
                              Validation Paiement
                            </Button>
                          ) : (
                            <Button
                              type="primary"
                              icon={<DollarCircleOutlined />}
                              onClick={() => handleMontantVerse(transaction)}
                              block
                            >
                              Montant Versé
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
              <CheckCircleOutlined className="text-green-400 text-5xl mb-4" />
              <Text className="text-gray-600 text-lg block mb-2">Tous les montants sont réglés</Text>
              <Text className="text-gray-400">
                Aucun montant restant à régler pour {fullName}
              </Text>
            </div>
          )}
        </Card>
      </div>
    );
  };

  // ==================== RENDER PRINCIPAL ====================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2">
          <div className="py-6">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item onClick={() => navigate('/')}>
                <HomeOutlined />
              </Breadcrumb.Item>
              <Breadcrumb.Item onClick={() => navigate('/clients')}>
                Clients
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {fullName}
              </Breadcrumb.Item>
            </Breadcrumb>
           
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center">
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/clients')}
                  className="mr-4 hover:bg-gray-100 rounded-lg"
                  size="large"
                >
                  Retour
                </Button>
                <div className="flex items-center">
                  <Avatar
                    size={64}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white mr-4 shadow-lg"
                    icon={<UserOutlined />}
                    style={{ fontSize: '24px' }}
                  />
                  <div>
                    <Title level={2} className="m-0 text-gray-900">
                      {fullName}
                    </Title>
                    <div className="flex items-center space-x-2 mt-1">
                      <Text className="text-gray-500">
                        <IdcardOutlined className="mr-1" />
                        Client #{(client.id || client._id || '').toString().substring(0, 8).toUpperCase()}
                      </Text>
                      <Tag color={getClientStatusColor()} className="ml-2">
                        {getClientStatusText()}
                      </Tag>
                      <Tag color="blue" className="ml-2">
                        <ShoppingOutlined className="mr-1" />
                        {stats.totalVentes} ventes
                      </Tag>
                      {stats.montantRestant > 0 && (
                        <Tag color="orange" className="ml-2">
                          <WalletOutlined className="mr-1" />
                          {stats.montantRestant.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })} à régler
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setEditModalVisible(true)}
                >
                  Modifier
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={redirectToNewSale}
                >
                  Nouvelle Vente
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-2">
        <Card className="shadow-sm" style={{ borderRadius: '2px', border: '1px solid #0073ff'}}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="client-detail-tabs"
            items={[
              {
                key: 'info',
                label: (
                  <span>
                    <InfoCircleOutlined />
                    <span className="ml-2">Informations</span>
                  </span>
                ),
                children: renderInfoTab()
              },
              {
                key: 'transactions',
                label: (
                  <span>
                    <TransactionOutlined />
                    <span className="ml-2">Transactions</span>
                    {transactions.length > 0 && (
                      <Badge count={transactions.length} style={{ marginLeft: 8 }} />
                    )}
                  </span>
                ),
                children: renderTransactionsTab()
              },
              {
                key: 'validation',
                label: (
                  <span>
                    <WalletOutlined />
                    <span className="ml-2">Versements</span>
                    {Array.isArray(transactions) && transactions.filter(t => t.statut === 'Validée' && t.montantRestant > 0).length > 0 && (
                      <Badge count={transactions.filter(t => t.statut === 'Validée' && t.montantRestant > 0).length} style={{ marginLeft: 8 }} />
                    )}
                  </span>
                ),
                children: renderValidationTab()
              }
            ]}
          />
        </Card>
      </div>

      {/* Edit Client Modal */}
      <Modal
        title="Modifier le client"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            prenom: client.prenom,
            nom: client.nom,
            telephone: client.telephone,
            adresse: client.adresse,
            email: client.email || ''
          }}
          onFinish={updateClient}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="prenom"
                label="Prénom"
                rules={[{ required: true, message: 'Veuillez saisir le prénom' }]}
              >
                <Input placeholder="Prénom" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nom"
                label="Nom"
                rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
              >
                <Input placeholder="Nom" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="telephone"
            label="Téléphone"
            rules={[
              { required: true, message: 'Veuillez saisir le téléphone' }
            ]}
          >
            <Input
              placeholder="Ex: 771234567"
              prefix={<PhoneOutlined className="text-gray-400" />}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Email invalide' }
            ]}
          >
            <Input
              placeholder="exemple@email.com"
              prefix={<MailOutlined className="text-gray-400" />}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="adresse"
            label="Adresse"
            rules={[{ required: true, message: 'Veuillez saisir l\'adresse' }]}
          >
            <TextArea
              placeholder="Adresse complète"
              rows={3}
              size="large"
            />
          </Form.Item>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <Button
              onClick={() => setEditModalVisible(false)}
              size="large"
              className="px-6"
            >
              Annuler
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="px-6"
            >
              Enregistrer
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MODAL : VersementModal */}
      <VersementModal
        open={versementModalVisible}
        onClose={() => {
          setVersementModalVisible(false);
          setSelectedVenteForVersement(null);
        }}
        onSubmit={handleVersementSubmit}
        vente={selectedVenteForVersement}
        clientNom={client?.nom}
        clientPrenom={client?.prenom}
        montantTotal={selectedVenteForVersement?.montant || 0}
        montantDejaVerse={selectedVenteForVersement?.montantVerse || 0}
        montantRestant={selectedVenteForVersement?.montantRestant || 0}
        numeroFacture={selectedVenteForVersement?.numero || ''}
      />

      {/* MODAL : Validation Paiement Complet */}
      <Modal
        title="Validation du paiement complet"
        open={validationPaiementModalVisible}
        onCancel={() => {
          setValidationPaiementModalVisible(false);
          setSelectedVenteForValidation(null);
          validationPaiementForm.resetFields();
        }}
        footer={null}
        width={600}
        centered
      >
        {selectedVenteForValidation && (
          <div className="mb-6">
            <Alert
              message="Validation finale"
              description="Vous êtes sur le point de valider le paiement complet de cette vente."
              type="warning"
              showIcon
              className="mb-4"
            />
           
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center mb-2">
                <Text strong>Vente:</Text>
                <Text>{selectedVenteForValidation.designation}</Text>
              </div>
              <div className="flex justify-between items-center mb-2">
                <Text strong>N° Facture:</Text>
                <Text className="font-mono">{selectedVenteForValidation.numero}</Text>
              </div>
              <div className="flex justify-between items-center mb-2">
                <Text strong>Montant total:</Text>
                <Text className="text-blue-600 font-bold">
                  {(selectedVenteForValidation.montant || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                </Text>
              </div>
              <div className="flex justify-between items-center mb-2">
                <Text strong>Montant versé:</Text>
                <Text className="text-green-600 font-bold">
                  {(selectedVenteForValidation.montantVerse || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                </Text>
              </div>
              <div className="flex justify-between items-center">
                <Text strong>Montant restant:</Text>
                <Text className="text-orange-600 font-bold">
                  {(selectedVenteForValidation.montantRestant || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                </Text>
              </div>
            </div>
          </div>
        )}

        <Form
          form={validationPaiementForm}
          layout="vertical"
          onFinish={validerPaiementComplet}
          initialValues={{
            modePaiement: 'Espèces',
            datePaiement: dayjs()
          }}
        >
          <Form.Item
            name="modePaiement"
            label="Mode de paiement"
            rules={[{ required: true, message: 'Veuillez sélectionner le mode de paiement' }]}
          >
            <Select placeholder="Sélectionnez le mode de paiement">
              <Option value="Espèces">Espèces</Option>
              <Option value="Chèque">Chèque</Option>
              <Option value="Virement">Virement</Option>
              <Option value="Carte">Carte bancaire</Option>
              <Option value="Mobile money">Mobile money</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="nomBanque"
            label="Nom de la banque (si applicable)"
          >
            <Input placeholder="Ex: BICIS, BOA, etc." />
          </Form.Item>

          <Form.Item
            name="numeroReference"
            label="Numéro de référence"
          >
            <Input placeholder="Numéro de chèque, référence virement, etc." />
          </Form.Item>

          <Form.Item
            name="datePaiement"
            label="Date du paiement"
          >
            <DatePicker format="DD/MM/YYYY" className="w-full" />
          </Form.Item>

          <Form.Item
            name="note"
            label="Note (optionnel)"
          >
            <TextArea rows={3} placeholder="Informations supplémentaires sur ce paiement..." />
          </Form.Item>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <Button
              onClick={() => {
                setValidationPaiementModalVisible(false);
                setSelectedVenteForValidation(null);
                validationPaiementForm.resetFields();
              }}
              size="large"
              className="px-6"
            >
              Annuler
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="px-6"
              icon={<CheckCircleOutlined />}
            >
              Valider le paiement
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientDetailPage;